/**
 * Where each camera of a group stands after a bulk settings change (item 226,
 * task 14). The server's settings_state is the same value that drives the
 * per-camera "Not applied yet" markers; this only adds two dashboard views of
 * it: "can't confirm" (pending or sent, but silent for too long) and "not HTTP".
 * Item 396: 'sent' = the camera fetched the change, its menu report has not
 * confirmed it yet; it waits for that report exactly like a pending camera.
 */

import type { GroupSettingsStatusCamera } from '@/types'

// A camera that has not reported its settings for this long will not confirm
// a change any time soon — flag it so nobody waits on it.
export const STALE_REPORT_MS = 3 * 24 * 60 * 60 * 1000

export function reportIsStale(reportedAt: string | null, now: number): boolean {
  if (!reportedAt) return true
  const t = Date.parse(reportedAt)
  return Number.isNaN(t) || now - t > STALE_REPORT_MS
}

export type SettingsStatusClass = 'applied' | 'sent' | 'pending' | 'cant_confirm' | 'none' | 'not_http'

export function classify(camera: GroupSettingsStatusCamera, now: number): SettingsStatusClass {
  if (camera.ingestion_type !== 'http') return 'not_http'
  const waitingOnReport = camera.settings_state === 'pending' || camera.settings_state === 'sent'
  if (waitingOnReport && reportIsStale(camera.params_reported_at, now)) return 'cant_confirm'
  return camera.settings_state
}

export function summarize(cameras: GroupSettingsStatusCamera[], now: number): Record<SettingsStatusClass, number> {
  const counts: Record<SettingsStatusClass, number> = { applied: 0, sent: 0, pending: 0, cant_confirm: 0, none: 0, not_http: 0 }
  for (const c of cameras) counts[classify(c, now)]++
  return counts
}

// A pending field's PATCH path ('desired_extra_settings.time_zone',
// 'desired_pir_interval') to the settings key settingLabel knows.
export function settingKeyOfPendingField(field: string): string {
  if (field.startsWith('desired_extra_settings.')) return field.slice('desired_extra_settings.'.length)
  if (field.startsWith('desired_')) return field.slice('desired_'.length)
  return field
}
